import { apiFetch } from "@/lib/auth-api";
import type {
	CentralMarginsResponse,
	MarginCalculatorConfig,
	MarginCalculatorConfigResponse,
	MarginCalculatorResultsParams,
	MarginCalculatorResultsResponse,
	MarginPartnerBreakdownResponse,
} from "./types";

const BASE = "/strateg-cenowy/margin-calculator";

export async function fetchMarginCalculatorConfig(
	idfirm?: number,
): Promise<MarginCalculatorConfigResponse> {
	const qs = idfirm != null ? `?idfirm=${idfirm}` : "";
	return apiFetch<MarginCalculatorConfigResponse>(`${BASE}/config${qs}`);
}

export async function updateMarginCalculatorConfig(
	config: MarginCalculatorConfig,
	idfirm?: number,
): Promise<MarginCalculatorConfigResponse> {
	return apiFetch<MarginCalculatorConfigResponse>(`${BASE}/config`, {
		method: "PUT",
		body: JSON.stringify(idfirm != null ? { ...config, idfirm } : config),
	});
}

export async function deleteMarginCalculatorOverride(
	idfirm: number,
): Promise<MarginCalculatorConfigResponse> {
	return apiFetch<MarginCalculatorConfigResponse>(`${BASE}/config?idfirm=${idfirm}`, {
		method: "DELETE",
	});
}

/** Marże centralne (Farmaster 1) dla apteki — tylko do podglądu w tabeli widełek. */
export async function fetchCentralMargins(idfirm: number): Promise<CentralMarginsResponse> {
	return apiFetch<CentralMarginsResponse>(`${BASE}/central-margins?idfirm=${idfirm}`);
}

export interface ProductMarginOverrideResponse {
	idfirm: number;
	bloz07: string;
	/** `null` po usunięciu nadpisania (powrót do marży z widełek). */
	margin: number | null;
	isOverride: boolean;
}

/** Upsert nadpisania marży docelowej dla (apteka, BLOZ) — ułamek (0.25 = 25%). */
export async function upsertProductMarginOverride(
	idfirm: number,
	bloz07: string,
	margin: number,
): Promise<ProductMarginOverrideResponse> {
	return apiFetch<ProductMarginOverrideResponse>(`${BASE}/product-override`, {
		method: "PUT",
		body: JSON.stringify({ idfirm, bloz07, margin }),
	});
}

export async function deleteProductMarginOverride(
	idfirm: number,
	bloz07: string,
): Promise<ProductMarginOverrideResponse> {
	return apiFetch<ProductMarginOverrideResponse>(
		`${BASE}/product-override?idfirm=${idfirm}&bloz07=${encodeURIComponent(bloz07)}`,
		{ method: "DELETE" },
	);
}

export async function fetchMarginCalculatorResults(
	params: MarginCalculatorResultsParams,
	signal?: AbortSignal,
): Promise<MarginCalculatorResultsResponse> {
	const sp = new URLSearchParams();
	// Wszystkie zdefiniowane parametry (scope, daty, search, membership oraz
	// filtry kolumnowe z MarginColumnFilterParams) — nazwy pól = nazwy query
	// params backendu (camelCase, serde rename_all).
	for (const [key, value] of Object.entries(params)) {
		if (value === undefined || value === null || value === "") continue;
		sp.set(key, String(value));
	}
	return apiFetch<MarginCalculatorResultsResponse>(`${BASE}/results?${sp.toString()}`, { signal });
}

/** Dane kafla „Programy partnerskie" — osobne, późniejsze żądanie (ciężka
 *  atrybucja korekt liczy się poza `/results`, tabela na nią nie czeka). */
export async function fetchMarginPartnerBreakdown(params: {
	scope: string;
	dateFrom?: string;
	dateTo?: string;
}): Promise<MarginPartnerBreakdownResponse> {
	const sp = new URLSearchParams();
	for (const [key, value] of Object.entries(params)) {
		if (value === undefined || value === null || value === "") continue;
		sp.set(key, String(value));
	}
	return apiFetch<MarginPartnerBreakdownResponse>(`${BASE}/partner-breakdown?${sp.toString()}`);
}
